/** @jsx jsx */
import React from 'react';
import { css, jsx } from '@compiled/react';
import { defineMessages, FormattedMessage, useIntl } from 'react-intl';

import AiGenerativeRemixIcon from '@atlaskit/icon-lab/core/ai-generative-remix';
import { ToolbarButton } from '@atlaskit/editor-toolbar/toolbar-button';
import { ToolbarButtonGroup } from '@atlaskit/editor-toolbar/toolbar-button-group';
import { ToolbarTooltip } from '@atlaskit/editor-toolbar/toolbar-tooltip';

import { UNSAFE_expValEqualsNoExposure } from '@confluence/feature-experiments';
import { fg } from '@confluence/feature-gating';

export const HIGHLIGHT_ACTIONS_REMIX_TRIGGER_SELECTOR =
	'[data-highlight-actions-remix-trigger="true"]';

const remixTriggerWrapperStyle = css({
	display: 'inline-flex',
});

type RemixDisabledReason = 'archived' | 'version-history';

const messages = defineMessages({
	remixInProgressTooltip: {
		id: 'highlight-actions.remix-action.in-progress-tooltip',
		defaultMessage: 'Remix in progress...',
		description:
			'Tooltip shown on the disabled Remix button when a remix session is already in progress',
	},
	remixToolbarTooltip: {
		id: 'highlight-actions.remix-action.toolbar-tooltip',
		defaultMessage: 'Remix',
		description:
			'Tooltip for the Remix button in the view page highlight actions inline toolbar that opens a modal to remix or regenerate the block content',
	},
	remixToolbarTooltipUpdated: {
		id: 'highlight-actions.remix-action.toolbar-tooltip.updated',
		defaultMessage: 'Create a diagram, infographic, and more from this content',
		description:
			'Tooltip for the Remix button in the view page highlight actions inline toolbar that opens a modal to remix or regenerate the block content',
	},
	remixToolbarLabel: {
		id: 'highlight-actions.remix-action.toolbar-label',
		defaultMessage: 'Remix',
		description:
			'Label for the Remix button in the view page highlight actions inline toolbar that opens a modal to remix or regenerate the block content',
	},
	remixDisabledTooltipArchived: {
		id: 'highlight-actions.remix-action.disabled-tooltip-archived',
		// oxlint-disable-next-line eslint-core/no-restricted-syntax
		defaultMessage: "Content on archived pages can't be remixed",
		description: 'Tooltip shown on the disabled Remix button when the page is archived',
	},
	remixDisabledTooltipVersionHistory: {
		id: 'highlight-actions.remix-action.disabled-tooltip-version-history',
		defaultMessage: 'Remix is unavailable in version history',
		description:
			'Tooltip shown on the disabled Remix button when viewing a previous version of the page',
	},
});

const getRemixDisabledTooltip = (
	formatMessage: ReturnType<typeof useIntl>['formatMessage'],
	reason?: RemixDisabledReason,
): string => {
	switch (reason) {
		case 'version-history':
			return formatMessage(messages.remixDisabledTooltipVersionHistory);
		case 'archived':
		default:
			return formatMessage(messages.remixDisabledTooltipArchived);
	}
};

export const RemixAction = ({
	onClick,
	onMouseDown,
	isDisabled,
	isRemixInProgress,
	remixDisabledReason,
}: {
	onClick?: () => void;
	onMouseDown?: (event: React.MouseEvent<HTMLElement>) => void;
	isDisabled?: boolean;
	isRemixInProgress?: boolean;
	remixDisabledReason?: RemixDisabledReason;
}): React.JSX.Element => {
	const { formatMessage } = useIntl();

	const labelMessage = messages.remixToolbarLabel;

	let tooltipContent: string;
	if (isRemixInProgress) {
		tooltipContent = formatMessage(messages.remixInProgressTooltip);
	} else if (isDisabled) {
		tooltipContent = getRemixDisabledTooltip(formatMessage, remixDisabledReason);
	} else {
		tooltipContent = formatMessage(
			// oxlint-disable-next-line confluence-feature-gating/no-new-feature-gates
			fg('cc_maui_updated_remix_tooltip_text')
				? messages.remixToolbarTooltipUpdated
				: messages.remixToolbarTooltip,
		);
	}

	const button = (
		<ToolbarButtonGroup>
			<ToolbarButton
				iconBefore={<AiGenerativeRemixIcon label="" size="small" />}
				testId="highlight-actions-remix"
				onClick={onClick}
				isDisabled={isDisabled || isRemixInProgress}
			>
				<FormattedMessage {...labelMessage} />
			</ToolbarButton>
		</ToolbarButtonGroup>
	);

	if (!UNSAFE_expValEqualsNoExposure('cc-ce-maui-remix-menu-multivariant', 'cohort', 'variant1')) {
		return <ToolbarTooltip content={tooltipContent}>{button}</ToolbarTooltip>;
	}

	// The anchored menu opens from this trigger, so the text selection it remixes must
	// survive the click. Preventing the default mousedown keeps the selection intact;
	// the ADS button does not forward mousedown in every toolbar variant, so capture it
	// on a wrapper instead.
	const handleMouseDownCapture = (event: React.MouseEvent<HTMLElement>) => {
		event.preventDefault();
		onMouseDown?.(event);
	};

	return (
		<ToolbarTooltip content={tooltipContent}>
			<span
				css={remixTriggerWrapperStyle}
				data-highlight-actions-remix-trigger="true"
				onMouseDownCapture={handleMouseDownCapture}
			>
				{button}
			</span>
		</ToolbarTooltip>
	);
};
